import { PaymentConfig } from '../types/questionnaire.types';

/**
 * The charge amount (in pence) a questionnaire's payment config resolves to for a
 * given referral: a per-referral override when one is configured, otherwise the
 * questionnaire's base amount.
 *
 * Mirrors PaymentService.calculate_payment_amount on the backend, which is what
 * actually charges — this is only for display, so the figure shown matches the
 * figure taken.
 */
export const resolvePaymentAmount = (
  paymentConfig?: PaymentConfig,
  referralId?: number | null
): number | undefined => {
  if (!paymentConfig) return undefined;

  // Guard the shape: configs authored before per-referral amounts moved to a
  // list of {referral_id, amount} may still hold another shape — ignore those.
  const overrides = Array.isArray(paymentConfig.amount_by_referral)
    ? paymentConfig.amount_by_referral
    : [];

  if (referralId != null) {
    const override = overrides.find((entry) => entry?.referral_id === referralId);
    if (override?.amount != null) return override.amount;
  }

  return paymentConfig.amount;
};

/**
 * The discount copy configured for a given referral, shown under the total in the
 * payment summary box. Undefined when the referral has no text configured — there is
 * no questionnaire-wide fallback, discount text is per-referral by definition.
 */
export const resolveDiscountText = (
  paymentConfig?: PaymentConfig,
  referralId?: number | null
): string | undefined => {
  if (!paymentConfig || referralId == null) return undefined;

  const overrides = Array.isArray(paymentConfig.amount_by_referral)
    ? paymentConfig.amount_by_referral
    : [];

  const text = overrides.find((entry) => entry?.referral_id === referralId)?.discount_text;
  return text?.trim() ? text : undefined;
};

export default resolvePaymentAmount;
